import { useCallback, useEffect, useState } from 'react';

import {
  addRecent,
  RECENT_STORAGE_KEY,
  readRecent,
  writeRecent,
  type RecentEntity,
  type RecentEntityKind,
} from '../utils/recent-entities';

/** Notifies same-tab listeners; `storage` events only fire in *other* tabs. */
const CHANGED_EVENT = 'admin-v2:recent-entities-changed';

/**
 * Read the recent-entity trail, kept in sync across tabs and components
 * (GDN-2777).
 *
 * The sidebar and the recorder live in different subtrees, so a plain
 * `useState` per consumer would leave the sidebar stale until remount. This
 * listens to both the cross-tab `storage` event and a same-tab custom event.
 */
export function useRecentEntities(): RecentEntity[] {
  const [entities, setEntities] = useState<RecentEntity[]>(() =>
    typeof window === 'undefined' ? [] : readRecent(window.localStorage),
  );

  useEffect(() => {
    const refresh = () => setEntities(readRecent(window.localStorage));
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === RECENT_STORAGE_KEY) refresh();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CHANGED_EVENT, refresh);
    };
  }, []);

  return entities;
}

/**
 * Record a visit once the entity's name is known.
 *
 * Callers pass `undefined` while loading rather than an id-only placeholder —
 * a trail entry reading "Organization" helps nobody, and recording early would
 * pin that placeholder as the remembered name.
 */
export function useRecordRecentEntity(
  kind: RecentEntityKind,
  id: string | undefined,
  name: string | undefined,
): void {
  useEffect(() => {
    if (!id || !name) return;
    const next = addRecent(readRecent(window.localStorage), {
      kind,
      id,
      name,
      visitedAt: Date.now(),
    });
    writeRecent(window.localStorage, next);
    window.dispatchEvent(new Event(CHANGED_EVENT));
  }, [kind, id, name]);
}

/** Clear the trail (used by the sidebar's dismiss affordance). */
export function useClearRecentEntities(): () => void {
  return useCallback(() => {
    writeRecent(window.localStorage, []);
    window.dispatchEvent(new Event(CHANGED_EVENT));
  }, []);
}
